/**
 * Web Vitals reporter — Milestone 1 (Observability)
 *
 * Captures LCP, INP, CLS, FCP, TTFB using the web-vitals library and:
 *  1. Stores each measurement in the `web_vitals` Supabase table
 *  2. Reports to Sentry as a performance measurement (enriches transactions)
 *
 * Rules:
 *  - Only runs in production builds
 *  - Fully consent-gated (skipped if analytics consent not granted)
 *  - Never throws, never blocks render
 *  - Dynamically imported to stay off the critical path
 *
 * CLS is stored as integer (raw × 1000) so all values share the same
 * integer column without precision loss (0.05 → 50).
 */

import { supabase } from '@/integrations/supabase/client';
import { consentAllowsAnalytics } from '@/lib/consent';

// Google Core Web Vitals thresholds (stored-value units)
const THRESHOLDS: Record<string, [number, number]> = {
  LCP:  [2500, 4000],  // ms
  INP:  [200,  500],   // ms
  CLS:  [100,  250],   // ×1000: 0.1→100 good, 0.25→250 ni
  FCP:  [1800, 3000],  // ms
  TTFB: [800,  1800],  // ms
};

export function vitalsRating(name: string, storedValue: number): 'good' | 'needs-improvement' | 'poor' {
  const [good, ni] = THRESHOLDS[name] ?? [Infinity, Infinity];
  if (storedValue <= good) return 'good';
  if (storedValue <= ni) return 'needs-improvement';
  return 'poor';
}

export function vitalsThresholds(name: string): [number, number] {
  return THRESHOLDS[name] ?? [Infinity, Infinity];
}

function toStoredValue(name: string, raw: number): number {
  if (name === 'CLS') return Math.round(raw * 1000);
  return Math.round(raw);
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getConnectionType(): string | null {
  return (navigator as any).connection?.effectiveType ?? null;
}

async function send(name: string, rawValue: number): Promise<void> {
  if (!consentAllowsAnalytics()) return;
  const value = toStoredValue(name, rawValue);
  const rating = vitalsRating(name, value);

  // 1. Persist to DB (backup / admin visibility)
  void supabase.from('web_vitals').insert({
    name,
    value,
    rating,
    page: window.location.pathname,
    device_type: getDeviceType(),
    connection_type: getConnectionType(),
  });

  // 2. Forward to Sentry performance measurement
  void import('@sentry/react').then((Sentry) => {
    Sentry.setMeasurement(
      name.toLowerCase(),
      rawValue,
      name === 'CLS' ? '' : 'millisecond',
    );
  }).catch(() => {});
}

let initialized = false;

export function initWebVitals(): void {
  if (initialized) return;
  if (import.meta.env.MODE !== 'production') return;
  initialized = true;

  void import('web-vitals').then(({ onLCP, onINP, onCLS, onFCP, onTTFB }) => {
    onLCP((m) => void send(m.name, m.value).catch(() => {}));
    onINP((m) => void send(m.name, m.value).catch(() => {}));
    onCLS((m) => void send(m.name, m.value).catch(() => {}));
    onFCP((m) => void send(m.name, m.value).catch(() => {}));
    onTTFB((m) => void send(m.name, m.value).catch(() => {}));
  }).catch(() => {});
}
